// Connection + flow-enabled store.
//
// Today: localStorage-backed for the static cockpit.
// Tomorrow: same shape, swapped to /api/connections — the agent's
// multi-tenant credential proxy keeps credentials per-user + per-studio.
// The cockpit's surface doesn't change; only the body of these
// functions does.
//
// CLAUDE.md §1 — keys are env-driven; no literal localStorage names.

import { env } from "./env";

export type ConnectionStatus = "disconnected" | "connecting" | "connected" | "error";

export interface ConnectionRecord {
  app: string;          // matches AGENTIC_INTEGRATIONS.name
  status: ConnectionStatus;
  account?: string;     // optional account label (e.g. email used)
  connectedAt?: string; // ISO timestamp
  message?: string;     // error reason when status = "error"
}

type ConnectionMap = Record<string, ConnectionRecord>;
type FlowEnabledMap = Record<string, boolean>;

function readMap<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}
function writeMap<T>(key: string, value: T): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

// ── connections ─────────────────────────────────────────────────────
export function getConnections(): ConnectionMap {
  return readMap<ConnectionMap>(env.VITE_LS_CONNECTIONS, {});
}
export function getConnection(app: string): ConnectionRecord {
  return getConnections()[app] ?? { app, status: "disconnected" };
}
export function setConnection(record: ConnectionRecord): void {
  const map = getConnections();
  map[record.app] = record;
  writeMap(env.VITE_LS_CONNECTIONS, map);
}
export function disconnect(app: string): void {
  const map = getConnections();
  delete map[app];
  writeMap(env.VITE_LS_CONNECTIONS, map);
}

// Per-auth-method submission — every branch hits a real Pages Function
// that proxies to the in-cluster agent (which encrypts + persists via
// AES-256-GCM + pgvector), or to n8n's REST API directly until the
// agent ships. NO local simulator. If the backend isn't reachable the
// chip stays "error" with the real reason — never silently "connected".

/** Build a human-readable error from a non-ok HTTP response. FastAPI
 *  returns `{"detail": "..."}`; Cloudflare Pages returns either an
 *  HTML 404 page or `{"error": "..."}`. Strip the JSON wrapper and
 *  attach context (HTTP status + endpoint). CLAUDE.md §5 — surface
 *  the real reason, never raw JSON, never silently swallow. */
function explainHttpError(status: number, statusText: string, body: string, url: string): string {
  let detail = "";
  if (body) {
    try {
      const parsed = JSON.parse(body) as { detail?: unknown; error?: unknown; message?: unknown };
      if (typeof parsed.detail === "string") detail = parsed.detail;
      else if (typeof parsed.error === "string") detail = parsed.error;
      else if (typeof parsed.message === "string") detail = parsed.message;
    } catch {
      // Not JSON — use raw text up to a sensible cap so HTML pages don't
      // dump into the toast.
      detail = body.length > 200 ? body.slice(0, 200) + "…" : body;
    }
  }
  if (status === 404) {
    // The agent's /api/connections route isn't bound yet — say so plainly
    // rather than parroting FastAPI's "Not Found" JSON. The connection
    // scaffold is in place; the n8n credential proxy ships when the
    // agent deploys.
    return `Connection backend not yet bound (HTTP 404 on ${url}). The credential-proxy endpoint ships when the agent deploys to the cluster; OAuth scaffold is wired and ready.`;
  }
  if (status === 503) {
    return `Connection service is temporarily unavailable (HTTP 503). ${detail || "Try again in a moment."}`;
  }
  if (status >= 500) {
    return `Server error ${status} ${statusText}${detail ? ` — ${detail}` : ""}.`;
  }
  if (status === 401 || status === 403) {
    return `Not authorised (HTTP ${status})${detail ? ` — ${detail}` : ""}. Sign out and back in if this persists.`;
  }
  return detail || `${statusText || `HTTP ${status}`}`;
}

async function postJson<T>(url: string, body: unknown): Promise<T> {
  const r = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const text = await r.text();
  if (!r.ok) throw new Error(explainHttpError(r.status, r.statusText, text, url));
  return JSON.parse(text) as T;
}

/** OAuth2 — returns the URL to open in a popup. The popup itself
 *  navigates to the provider, completes consent, then the provider
 *  redirects back to /api/connect/callback which closes the popup
 *  and flips state. */
export async function initiateOAuth(app: string): Promise<{ initiate_url: string }> {
  setConnection({ app, status: "connecting" });
  return await postJson<{ initiate_url: string }>(`/api/connections/${encodeURIComponent(app)}`, { auth: "oauth2" });
}

/** API key (Stripe sk_live_..., Twilio Account SID + Auth Token, etc.).
 *  Server-side: encrypt at rest with AES-256-GCM, insert into
 *  n8n_credentials, return the public-safe Connection record. */
export async function submitApiKey(app: string, key: string): Promise<void> {
  setConnection({ app, status: "connecting" });
  const res = await postJson<{ ok: boolean; account_label?: string; error?: string }>(
    `/api/connections/${encodeURIComponent(app)}`,
    { auth: "api_key", key },
  );
  if (!res.ok) throw new Error(res.error || "Backend refused the key.");
  setConnection({
    app, status: "connected",
    ...(res.account_label ? { account: res.account_label } : {}),
    connectedAt: new Date().toISOString(),
  });
}

/** Basic auth — username + password. Used only for legacy hosts that
 *  don't support OAuth (IMAP/SMTP, some SOAP gateways). Stored
 *  encrypted; never travels in plaintext after this POST. */
export async function submitBasicAuth(app: string, user: string, password: string): Promise<void> {
  setConnection({ app, status: "connecting" });
  const res = await postJson<{ ok: boolean; account_label?: string; error?: string }>(
    `/api/connections/${encodeURIComponent(app)}`,
    { auth: "basic_auth", user, password },
  );
  if (!res.ok) throw new Error(res.error || "Backend refused the credentials.");
  setConnection({
    app, status: "connected",
    account: res.account_label ?? user,
    connectedAt: new Date().toISOString(),
  });
}

/** Service-account JSON — Google Workspace, Azure SP, etc. The whole
 *  JSON blob is sent server-side once, encrypted at rest, then only
 *  the fingerprint (client_email + sha256) is shown back to the user. */
export async function submitServiceAccount(app: string, json: unknown): Promise<void> {
  setConnection({ app, status: "connecting" });
  const res = await postJson<{ ok: boolean; account_label?: string; error?: string }>(
    `/api/connections/${encodeURIComponent(app)}`,
    { auth: "service_account", json },
  );
  if (!res.ok) throw new Error(res.error || "Backend refused the service account.");
  setConnection({
    app, status: "connected",
    ...(res.account_label ? { account: res.account_label } : {}),
    connectedAt: new Date().toISOString(),
  });
}

/** Webhook — server mints a unique URL + HMAC signing secret keyed
 *  to this studio. Paste both into the provider's webhook config;
 *  every incoming request's signature is verified against the secret. */
export async function fetchWebhook(app: string): Promise<{ url: string; secret: string }> {
  setConnection({ app, status: "connecting" });
  const res = await postJson<{ url: string; secret: string }>(
    `/api/connections/${encodeURIComponent(app)}`,
    { auth: "webhook" },
  );
  setConnection({
    app, status: "connected",
    account: new URL(res.url).pathname.split("/").pop() ?? app,
    connectedAt: new Date().toISOString(),
  });
  return res;
}

// ── flow enabled state ─────────────────────────────────────────────
export function getFlowEnabled(): FlowEnabledMap {
  return readMap<FlowEnabledMap>(env.VITE_LS_FLOW_ENABLED, {});
}
export function isFlowEnabled(flowId: string): boolean {
  const map = getFlowEnabled();
  // Flows default to enabled — operators turn off, not on.
  return map[flowId] !== false;
}
export function setFlowEnabled(flowId: string, enabled: boolean): void {
  const map = getFlowEnabled();
  map[flowId] = enabled;
  writeMap(env.VITE_LS_FLOW_ENABLED, map);
}

// Resolve readiness for a flow — connected vs disconnected required apps.
// The agent uses this to refuse politely when a required app is missing.
export interface FlowReadiness {
  ready: boolean;
  missing: readonly string[];
  total_required: number;
}
export function getFlowReadiness(required: readonly string[]): FlowReadiness {
  const conns = getConnections();
  const missing = required.filter((app) => conns[app]?.status !== "connected");
  return { ready: missing.length === 0, missing, total_required: required.length };
}
